<template>
    <div class="q-pa-sm MeterPanel">
        <div class="row items-center q-mb-sm MeterPanelHeader">
            <div class="row items-center flex justify-between w-full">
                <div class="text-primary">
                    {{ $t('visualization.customPlotPanels.durationMeter') }}
                </div>
                <q-btn dense flat color="primary" class="q-ml-xs">
                    <q-icon :name="dataScopeIcon(dataScope)" />
                    <q-icon right name="arrow_drop_down" />
                    <q-menu class="text-primary">
                        <q-list dense style="min-width:140px">
                            <q-item
                                v-close-popup
                                clickable
                                :active="dataScope === 'shown'"
                                activeClass="bg-primary text-white"
                                @click="dataScope = 'shown'"
                            >
                                <q-item-section avatar>
                                    <q-icon name="account_tree" />
                                </q-item-section>
                                <q-item-section>{{ $t('visualization.customPlotPanels.durationMeterInfo.shownCases') }}</q-item-section>
                            </q-item>

                            <q-item
                                v-close-popup
                                clickable
                                :active="dataScope === 'filtered'"
                                activeClass="bg-primary text-white"
                                @click="dataScope = 'filtered'"
                            >
                                <q-item-section avatar>
                                    <q-icon name="filter_alt" />
                                </q-item-section>
                                <q-item-section>{{ $t('visualization.customPlotPanels.durationMeterInfo.filteredData') }}</q-item-section>
                            </q-item>
                            <q-item
                                v-close-popup
                                clickable
                                :active="dataScope === 'global'"
                                activeClass="bg-primary text-white"
                                @click="dataScope = 'global'"
                            >
                                <q-item-section avatar>
                                    <q-icon name="table_rows" />
                                </q-item-section>
                                <q-item-section>{{ $t('visualization.customPlotPanels.durationMeterInfo.globalData') }}</q-item-section>
                            </q-item>
                        </q-list>
                    </q-menu>
                    <q-tooltip>
                        {{ $t('visualization.customPlotPanels.durationMeterInfo.dataScope') }}:
                        {{
                            dataScope === 'shown'
                                ? $t('visualization.customPlotPanels.durationMeterInfo.shownCases')
                                : dataScope === 'filtered'
                                    ? $t('visualization.customPlotPanels.durationMeterInfo.filteredData')
                                    : $t('visualization.customPlotPanels.durationMeterInfo.globalData')
                        }}
                    </q-tooltip>
                </q-btn>
            </div>
        </div>

        <div class="row q-col-gutter-md">
            <div class="col-12 col-md-auto MeterPanelOptions">
                <div class="row justify-center items-center q-mt-sm q-mb-sm">
                    <q-btn-toggle
                        v-model="stretchMode"
                        :options="stretchModeOptions"
                        toggleColor="primary"
                        textColor="primary"
                        outline
                        unelevated
                        dense
                        size="0.9em"
                        class="border-toggle"
                        noCaps
                    />
                    <q-tooltip>
                        {{ $t('visualization.customPlotPanels.durationMeterInfo.stretchModeTooltip') }}
                    </q-tooltip>
                </div>
                <div v-if="stretchMode === 'single'" class="row items-center q-mb-sm">
                    <q-select
                        v-model="stretchSelection"
                        dense
                        flat
                        class="flex-1"
                        :options="stretchOptions"
                        emitValue
                        mapOptions
                        :label="$t('visualization.customPlotPanels.durationMeterInfo.stretch')"
                        @update:model-value="getData"
                    />
                </div>
                <!-- Activity 1 -->
                <div class="row items-center q-mb-sm">
                    <q-select
                        v-model="selectedActivity1"
                        dense
                        flat
                        class="flex-1"
                        :options="activities"
                        emitValue
                        mapOptions
                        :label="$t('visualization.customPlotPanels.durationMeterInfo.activity1')"
                        @update:model-value="getData"
                    />
                    <q-btn dense flat color="primary" :icon="occIcon(activity1Occurrence)">
                        <q-menu class="text-primary">
                            <q-list dense style="min-width:150px">
                                <q-item v-close-popup clickable :active="activity1Occurrence === 'first'" activeClass="bg-primary text-white" @click="activity1Occurrence = 'first'">
                                    <q-item-section avatar>
                                        <q-icon name="first_page" />
                                    </q-item-section>
                                    <q-item-section>
                                        {{ $t('visualization.customPlotPanels.durationMeterInfo.firstOccurrence') }}
                                    </q-item-section>
                                </q-item>

                                <q-item v-close-popup clickable :active="activity1Occurrence === 'last'" activeClass="bg-primary text-white" @click="activity1Occurrence = 'last'">
                                    <q-item-section avatar>
                                        <q-icon name="last_page" />
                                    </q-item-section>
                                    <q-item-section>
                                        {{ $t('visualization.customPlotPanels.durationMeterInfo.lastOccurrence') }}
                                    </q-item-section>
                                </q-item>
                            </q-list>
                        </q-menu>
                        <q-tooltip>
                            {{
                                activity1Occurrence === 'first'
                                    ? $t('visualization.customPlotPanels.durationMeterInfo.firstOccurrence')
                                    : $t('visualization.customPlotPanels.durationMeterInfo.lastOccurrence')
                            }}
                        </q-tooltip>
                    </q-btn>
                </div>

                <!-- Activity 2 -->
                <div v-if="stretchMode === 'two'" class="row items-center q-mb-sm">
                    <q-select
                        v-model="selectedActivity2"
                        dense
                        flat
                        class="flex-1"
                        :options="activities"
                        emitValue
                        mapOptions
                        :label="$t('visualization.customPlotPanels.durationMeterInfo.activity2')"
                        @update:model-value="getData"
                    />
                    <q-btn dense flat color="primary" :icon="occIcon(activity2Occurrence)">
                        <q-menu class="text-primary">
                            <q-list dense style="min-width:150px">
                                <q-item v-close-popup clickable :active="activity2Occurrence === 'first'" activeClass="bg-primary text-white" @click="activity2Occurrence = 'first'">
                                    <q-item-section avatar>
                                        <q-icon name="first_page" />
                                    </q-item-section>
                                    <q-item-section>
                                        {{ $t('visualization.customPlotPanels.durationMeterInfo.firstOccurrence') }}
                                    </q-item-section>
                                </q-item>

                                <q-item v-close-popup clickable :active="activity2Occurrence === 'last'" activeClass="bg-primary text-white" @click="activity2Occurrence = 'last'">
                                    <q-item-section avatar>
                                        <q-icon name="last_page" />
                                    </q-item-section>
                                    <q-item-section>
                                        {{ $t('visualization.customPlotPanels.durationMeterInfo.lastOccurrence') }}
                                    </q-item-section>
                                </q-item>
                            </q-list>
                        </q-menu>
                        <q-tooltip>
                            {{
                                activity2Occurrence === 'first'
                                    ? $t('visualization.customPlotPanels.durationMeterInfo.firstOccurrence')
                                    : $t('visualization.customPlotPanels.durationMeterInfo.lastOccurrence')
                            }}
                        </q-tooltip>
                    </q-btn>
                </div>
            </div>

            <div class="col-12 col-md MeterPanelCharts">
                <div class="row justify-center q-mb-sm">
                    <q-btn-toggle
                        v-model="viewMode"
                        :options="[
                            { label: $t('visualization.customPlotPanels.durationMeterInfo.stats'), value: 'stats', icon: '123' },
                            { label: $t('visualization.customPlotPanels.durationMeterInfo.histogram'), value: 'hist', icon: 'bar_chart' },
                        ]"
                        toggleColor="primary"
                        textColor="primary"
                        noCaps
                        unelevated
                        outline
                        dense
                        size="0.9em"
                        padding="2px 10px"
                        class="border-toggle"
                    />
                </div>
                <div v-if="viewMode === 'stats'" class="chart-container">
                    <v-chart :option="statsChartOption" class="chart" />
                </div>

                <div v-if="viewMode === 'hist'" class="chart-container">
                    <div
                        v-if="histogram.length === 0"
                        class="text-black chart text-center q-mt-md"
                    >
                        {{ $t('visualization.customPlotPanels.durationMeterInfo.noData') }}
                    </div>
                    <div v-else>
                        <v-chart
                            ref="chart"
                            :option="durationMeterOptions"
                            class="chart"
                            autoresize
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import VueTypes from 'vue-types'
import { colors } from 'quasar'
import { dashboardChartsMixin, filtersMixin, filtersStorageMixin } from '@/mixins'
import {
    Api,
    apiRequest,
    notifyError,
} from '@/api'
import echartsColors from '@/charts/echartsColors'

const { getPaletteColor } = colors

export default {
    name: 'DurationMeterPanel',
    components: {},
    mixins: [dashboardChartsMixin, filtersMixin, filtersStorageMixin],
    props: {
        processId: VueTypes.string,
        filters: VueTypes.array,
        chart: VueTypes.object,
    },

    data() {
        return {
            activities: [],

            selectedActivity1: null,
            selectedActivity2: null,

            activity1Occurrence: 'first',
            activity2Occurrence: 'first',

            viewMode: 'stats',

            dataScope: 'shown',
            stretchMode: 'two',
            stretchSelection: true,

            histogram: [],
            range: '',

            statsChartOption: {
                tooltip: {
                    trigger: 'axis',
                    formatter: (params) => {
                        let tooltipContent = ''
                        params.forEach((param) => {
                            // const { seriesName } = param
                            const { value } = param
                            const { color } = param
                            tooltipContent += `<div style="width: 10px; height: 10px; background-color: ${color};border-radius: 50%; display: inline-block; margin-right: 3px;">
                                </div>`
                            tooltipContent += ` ${this.formatDuration(value, 2)}`
                            tooltipContent += '<br></div>'
                        })
                        return tooltipContent
                    },
                },
                legend: {
                    data: ['Min', 'Avg', 'Med', 'Max'],
                    top: 0,
                    left: 'center',
                    itemWidth: 10,
                    itemHeight: 6,
                    textStyle: { fontSize: 12 },
                },
                grid: {
                    left: 50, right: 25, top: 40, bottom: 20,
                },
                xAxis: { type: 'category', data: [''] },
                yAxis: {
                    type: 'value',
                    axisLabel: {
                        formatter: (value, index) => {
                            if (index === 0) return ''
                            return this.formatDuration(value, 1, 1)
                        },
                    },
                },
                color: [getPaletteColor('positive'), getPaletteColor('primary'), getPaletteColor('orange-7'), getPaletteColor('negative')],
                series: ['Min', 'Avg', 'Med', 'Max'].map((label) => ({
                    name: label,
                    type: 'bar',
                    data: [],
                    label: {
                        show: true,
                        position: 'top',
                        formatter: (value) => this.formatDuration(value.value, 1, 1),
                    },
                })),
            },
            durationMeterOptions: {
                tooltip: {
                    trigger: 'axis',
                    confine: true,
                    formatter: (params) => {
                        let tooltipContent = ''
                        params.forEach((param) => {
                            const { value } = param
                            const { color } = param
                            const { axisValueLabel } = param
                            tooltipContent += `<div><strong>${axisValueLabel}</strong><br>`
                            tooltipContent += `<div style="width: 10px; height: 10px; background-color: ${color};border-radius: 50%; display: inline-block; margin-right: 3px;">
                                </div>`
                            tooltipContent += `${value}`
                            tooltipContent += '<br></div>'
                        })
                        return tooltipContent
                    },
                },
                title: {
                    left: 'auto',
                    top: '5%',
                    textStyle: {
                        color: echartsColors.palette.blueMid,
                        fontSize: '1em',
                    },
                },
                xAxis: {
                    type: 'category',
                    data: [],
                    axisTick: { show: false },
                    axisLine: {
                        show: false,
                    },
                    axisLabel: {
                    },
                },
                yAxis: {
                    type: 'value',
                    showMinLabel: false,
                    axisLabel: {
                        formatter:
                            (value, index) => {
                                if (index === 0) return ''
                                if (value >= 1e6) return `${(value / 1e6).toFixed(1)}M`
                                if (value >= 1e3) return `${(value / 1e3).toFixed(1)}K`
                                return value
                            },
                    },
                },
                grid: {
                    left: 53,
                    right: 32,
                    bottom: 50,
                    top: 20,
                },
                series: {
                    name: this.$t('visualization.customPlotPanels.frequency'),
                    type: 'bar',
                    data: [],
                    itemStyle: {
                        borderRadius: [3, 3, 3, 3],
                        color: getPaletteColor('primary'),
                    },
                },
            },
        }
    },
    computed: {
        occIcon() {
            return (value) => (value === 'first' ? 'first_page' : 'last_page')
        },
        dataScopeIcon() {
            return (scope) => {
                if (scope === 'filtered') return 'filter_alt'
                if (scope === 'global') return 'table_rows'
                return 'account_tree'
            }
        },
        stretchModeOptions() {
            return [
                { label: this.$t('visualization.customPlotPanels.durationMeterInfo.twoActivityMode'), value: 'two', icon: 'layers' },
                { label: this.$t('visualization.customPlotPanels.durationMeterInfo.singleActivityMode'), value: 'single', icon: 'swap_vert' },
            ]
        },
        stretchOptions() {
            return [
                { label: this.$t('visualization.customPlotPanels.durationMeterInfo.startToActivity'), value: true },
                { label: this.$t('visualization.customPlotPanels.durationMeterInfo.activityToEnd'), value: false },
            ]
        },
    },
    watch: {
        dataScope: 'getData',
        stretchMode: 'getData',
        activity1Occurrence: 'getData',
        activity2Occurrence: 'getData',
        chart: {
            handler() {
                this.updateActivities()
                this.getData()
            },
            immediate: true,
            deep: true,
        },
    },

    mounted() {
        this.updateActivities()
        this.getData()
    },
    methods: {
        resetCharts() {
            this.histogram = []
            if (this.statsChartOption?.series?.length >= 4) {
                this.statsChartOption.series[0].data = []
                this.statsChartOption.series[1].data = []
                this.statsChartOption.series[2].data = []
                this.statsChartOption.series[3].data = []
            }

            this.durationMeterOptions.xAxis.data = []
            this.durationMeterOptions.series.data = []
        },
        updateActivities() {
            if (this.chart && Array.isArray(this.chart.activities)) {
                const newActivities = this.chart.activities
                    .filter((act) => act.name !== 'START' && act.name !== 'END')
                    .map((act) => act.name)
                this.activities = newActivities

                // Mantener selección previa si existe en la nueva lista
                if (newActivities.length > 0) {
                    this.selectedActivity1 = newActivities.includes(this.selectedActivity1)
                        ? this.selectedActivity1
                        : newActivities[0]
                    this.selectedActivity2 = newActivities.includes(this.selectedActivity2)
                        ? this.selectedActivity2
                        : newActivities[newActivities.length - 1]
                } else {
                    this.selectedActivity1 = null
                    this.selectedActivity2 = null
                }
            } else {
                this.activities = []
                this.selectedActivity1 = null
                this.selectedActivity2 = null
            }
        },
        getData() {
            // When there is no chart/activities (e.g. empty variants/loops result),
            // avoid calling the API with null params.
            if (!this.selectedActivity1) {
                this.resetCharts()
                return
            }
            if (this.stretchMode === 'two' && !this.selectedActivity2) {
                this.resetCharts()
                return
            }

            let params = {
                activityName1: this.selectedActivity1,
                firstOccurrenceAc1: this.activity1Occurrence === 'first',
            }
            if (this.stretchMode === 'single') {
                params = {
                    ...params,
                    fromFirstActivity: this.stretchSelection,
                }
            } else {
                params = {
                    ...params,
                    activityName2: this.selectedActivity2,
                    firstOccurrenceAc2: this.activity2Occurrence === 'first',
                }
            }
            if (this.dataScope === 'shown' && (this.chart.variants || this.chart.variantId || this.chart.loopId || this.chart.loopId === 0)) {
                params = {
                    ...params,
                    ...(this.chart.variantId ? { variantUid: this.chart.variantId } : {}),
                    ...(this.chart.variants ? { variantCount: this.chart.variants } : {}),
                    ...(this.chart.loopId || this.chart.loopId === 0 ? { loopUid: this.chart.loopId } : {}),
                }
            }
            if (this.dataScope !== 'global') {
                params = this.buildVisualizationParams(params)
            }
            const datasetUid = this.processId
            apiRequest(Api().visualizations.durationMetrics({ datasetUid, params }))
                .then((res) => {
                    this.histogram = res.histogram
                    // gráfico stats
                    this.statsChartOption.series[0].data = [res.min]
                    this.statsChartOption.series[1].data = [res.avg]
                    this.statsChartOption.series[2].data = [res.median]
                    this.statsChartOption.series[3].data = [res.max]
                    // gráfico histograma
                    const labels = res.histogram.map((bin) => `${this.formatDuration(bin.start, 1)} - ${this.formatDuration(bin.finish, 1)}`)
                    const frequencies = res.histogram.map((bin) => bin.frequency)
                    this.durationMeterOptions.xAxis.data = labels
                    this.durationMeterOptions.series.data = frequencies
                })
                .catch(notifyError)
        },
    },
}
</script>
<style lang="scss" scoped>
.MeterPanel {
    max-width: none;
}

.MeterPanelOptions {
    width: 240px;
}

.MeterPanelCharts {
    width: auto;
    min-width: 0;
}

.chart-container {
    width: 100%;

    .chart {
        width: 100%;
        height: 180px;
        margin-bottom: 5px;
    }
}


:deep(.border-toggle .q-btn[aria-pressed="true"]) {
    background: rgba($primary, 0.12) !important;
}

:deep(.border-toggle.q-btn-group--outline:before),
:deep(.border-toggle .q-btn--outline:before) {
    border-width: 1px !important;
}

</style>
